import React, {useMemo, useState} from 'react';
import { Button } from '@mui/material';
import SpaceBarIcon from '@mui/icons-material/SpaceBar';
import AddIcon from '@mui/icons-material/Add';
import {connect} from "react-redux";
import {FormattedMessage} from "react-intl";

import workspaceControllers from "../../../canvas/store/workspaceControllers";
import FeatureName from "../../../../../visComponents/featureName";
import {getCurrentLayout} from "../../../../../appLayout";
import {setFeaturePicker} from "../../../../../../actions/sqftMenuFunctions";
import {
  CABINET_FILTER_NAMES,
  CABINETS_TO_HANDLE,
  FEATURE_PICKER_TYPE,
  SQFT_FEATURES,
} from "../../../../constants";
import ConfirmButtons from "./ConfirmButtons";
import workspace from "../../../canvas/core/workspace";
import {updateCabinetHandlesIn3D} from "../../../ar3d/threeD/glbViewer/scene/modelsLoader/cabinetInterior/tallCabinetUpdate3D";

const Handles = (props) => {
  const {sqftActiveShapeId, featurePicker, setFeaturePicker, resourceControllerCounter, selectedFeatureInPicker} = props;
  const [isUpdatingHandles, setIsUpdatingHandles] = useState(false);

  const lineHandlesModels = useMemo(() => {
    return workspaceControllers.handles.getAllResourcesForTheShape(sqftActiveShapeId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resourceControllerCounter]);

  const hasHandles = lineHandlesModels.length > 0;

  const openHandlePicker = (lineHandlesModel, isAdding) => {
    const shape = workspace.getShapeById(sqftActiveShapeId);
    const handlePartId = lineHandlesModel?.partId
      || CABINETS_TO_HANDLE[shape?.partId]
      || SQFT_FEATURES.top_handle;

    const pickerData = {
      isOpen: true,
      partIds: [handlePartId],
      shapeId: sqftActiveShapeId,
      type: FEATURE_PICKER_TYPE.handles,
      isAdding: isAdding
    };

    if (!isAdding && lineHandlesModel) {
      pickerData.modelId = lineHandlesModel.id;
      pickerData.featureId = lineHandlesModel.featureId;
      pickerData.sharedFeatureName = lineHandlesModel.sharedFeatureName;
    }

    setFeaturePicker(pickerData);
  }

  const confirmCallback = async () => {
    const shape = workspace.getShapeById(sqftActiveShapeId);
    if (!shape || !shape.shapeCabinet) {
      return;
    }

    setIsUpdatingHandles(true);

    try {
      if (featurePicker.isAdding) {
        const selectedHandle = layout.getFeatureById(selectedFeatureInPicker);
        shape.shapeCabinet.generateAttachedFeatures(CABINET_FILTER_NAMES.knobs, { knobs: selectedHandle });
        workspaceControllers.hinge.updateReduxCounterOnChange();
      } else {
        const lineHandleModel = workspaceControllers.handles.getResourceById(featurePicker.modelId);
        const handlesFeatureGroup = layout.getFeatureGroupByPartId(lineHandleModel.partId);
        const selectedHandle = layout.getFeatureById(selectedFeatureInPicker);
        const handleFeature = handlesFeatureGroup.features.find(feature =>
          feature.getFeatureName() === selectedHandle.getFeatureName()
        );
        lineHandleModel.changeFeature(handleFeature);
        workspaceControllers.handles.updateReduxCounterOnChange();
      }

      await updateCabinetHandlesIn3D(sqftActiveShapeId);
    } catch (error) {
      console.error('[Cabinet handles] Could not update handles in 3D:', error);
    } finally {
      setFeaturePicker({ isOpen: false, partIds: [], shapeId: '', type: '', isAdding: false });
      setIsUpdatingHandles(false);
    }
  }

  const cancelCallback = () => {
    setFeaturePicker({ isOpen: false, partIds: [], shapeId: '', type: '', isAdding: false });
  }

  const layout = getCurrentLayout();

  return (
    <div className="sr__section sr__handles">
      <div className="sr__section-title">
        <FormattedMessage id="sqft.cabinets.attributes.handles"/>
      </div>
      {
        hasHandles ? (
          lineHandlesModels.map((lineHandlesModel, index) => {
            const handleFeature = layout.getFeatureById(lineHandlesModel.featureId, true);

            return (
              <div className="handles__item" key={index}>
                <label className="handles__label">
                  <FeatureName feature={handleFeature} />
                </label>
                {
                  featurePicker.isOpen
                  && featurePicker.modelId === lineHandlesModel.id
                  && featurePicker.type === FEATURE_PICKER_TYPE.handles
                    ? <ConfirmButtons
                      confirmCallback={confirmCallback}
                      cancelCallback={cancelCallback}
                      isLoading={isUpdatingHandles}
                    />
                    : <Button
                      className={"sr__button handles__button"}
                      onClick={() => openHandlePicker(lineHandlesModel, false)}
                      variant="outlined"
                      color="primary"
                      startIcon={<SpaceBarIcon style={{transform: 'rotate(180deg)'}}/>}
                    >
                      <FormattedMessage id="sqft.cabinets.attributes.changeHandle"/>
                    </Button>
                }
              </div>
            )
          })
        ) : (
          <div className="handles__item">
            {
              featurePicker.isOpen
              && featurePicker.type === FEATURE_PICKER_TYPE.handles
              && featurePicker.isAdding
                ? <ConfirmButtons
                  confirmCallback={confirmCallback}
                  cancelCallback={cancelCallback}
                  isLoading={isUpdatingHandles}
                />
                : <Button
                  className={"sr__button handles__button"}
                  onClick={() => openHandlePicker(null, true)}
                  variant="outlined"
                  color="primary"
                  startIcon={<AddIcon />}
                >
                  <FormattedMessage id="sqft.cabinets.attributes.addHandle"/>
                </Button>
            }
          </div>
        )
      }
    </div>
  );
};

const mapStateToProps = (store) => ({
  sqftActiveShapeId: store.sqftMenuState.sqftActiveShapeId,
  featurePicker: store.sqftMenuState.featurePicker,
  selectedFeatureInPicker: store.sqftMenuState.selectedFeatureInPicker,
  resourceControllerCounter: store.sqftResourceState.resourceControllerCounter,
});

const mapDispatchToProps = {
  setFeaturePicker
};

export default connect(mapStateToProps, mapDispatchToProps)(Handles);
